import { FC } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator } from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import { StyledSubOptionsContainer } from '../PdfExportConfigDialog.styles';
import {
  COMPANY_OPTION_CONFIGS,
  CompanyPdfOptions,
  VALUATION_APPROACH_OPTION_CONFIGS,
} from '../PdfExportConfigDialog.types';
import { CompanySubOptionsProps } from './SubOptions.types';

const ALL_OPTION_LABELS: Partial<Record<keyof CompanyPdfOptions, string>> = Object.fromEntries([
  ...COMPANY_OPTION_CONFIGS.map(({ key, label }) => [key, label]),
  ...VALUATION_APPROACH_OPTION_CONFIGS.map(({ key, label }) => [key, label]),
]);

interface SortableItemProps {
  id: keyof CompanyPdfOptions;
  label: string;
  checked: boolean;
  onChange: () => void;
}

const SortableItem: FC<SortableItemProps> = ({ id, label, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ display: 'flex', alignItems: 'center', userSelect: 'none' }}>
      <IconButton
        size="small"
        disableRipple
        sx={{ cursor: 'grab', color: 'action.active', p: 0.5, mr: 0.5 }}
        {...attributes}
        {...listeners}>
        <DragIndicator fontSize="small" />
      </IconButton>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={onChange}
            size="small"
          />
        }
        label={label}
        sx={{ m: 0, flex: 1 }}
      />
    </Box>
  );
};

export const CompanySubOptions = ({
  options,
  onOptionsChange,
  showCondition,
  optionsOrder,
  onOrderChange,
}: CompanySubOptionsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!showCondition) {
    return null;
  }

  const handleOptionChange = (optionKey: keyof CompanyPdfOptions) => {
    onOptionsChange({
      ...options,
      [optionKey]: !options[optionKey],
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = optionsOrder.indexOf(active.id as keyof CompanyPdfOptions);
      const newIndex = optionsOrder.indexOf(over.id as keyof CompanyPdfOptions);
      onOrderChange(arrayMove(optionsOrder, oldIndex, newIndex));
    }
  };

  const nonApproachKeys = new Set<keyof CompanyPdfOptions>(COMPANY_OPTION_CONFIGS.map(c => c.key));
  const approachKeys = new Set<keyof CompanyPdfOptions>(VALUATION_APPROACH_OPTION_CONFIGS.map(c => c.key));

  const visibleKeys = optionsOrder.filter(key => nonApproachKeys.has(key) || approachKeys.has(key));

  return (
    <StyledSubOptionsContainer>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext items={visibleKeys} strategy={verticalListSortingStrategy}>
          {visibleKeys.map(key => (
            <SortableItem
              key={key}
              id={key}
              label={ALL_OPTION_LABELS[key] ?? key}
              checked={options[key]}
              onChange={() => handleOptionChange(key)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </StyledSubOptionsContainer>
  );
};
